import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { T, cardSx, labelSx, chartTooltipStyle, axisTick, MetricCard, SectionLabel, EmptyNote } from "./PlatformAnalyticsShared";

const TREND_SERIES = [
  { key: "visits", name: "Recruiter logins", color: T.primary },
  { key: "applications", name: "Applications", color: T.blue },
  { key: "talent_applications", name: "Sent by talent team", color: T.purple },
  { key: "reveals", name: "CV reveals", color: T.amber },
  { key: "new_candidates", name: "New candidates", color: T.teal },
  { key: "new_companies", name: "New companies", color: T.red },
];

const NO_CREDITS_COLOR = "hsl(140 6% 80%)";
const HEALTH_COLORS = { active: T.green, at_risk: T.amber, dormant: T.red, email_openers: T.blue, old_client: T.dim, no_credits: NO_CREDITS_COLOR };
const HEALTH_LABELS = { active: "Active", at_risk: "At risk", dormant: "Dormant", email_openers: "Email openers", old_client: "Old clients", no_credits: "No credits" };

function HealthLegend({ color, title, children }) {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <Box sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: color, mt: "3px", flexShrink: 0 }} />
      <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, lineHeight: 1.45 }}>
        <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg }}>{title}: </Box>
        {children}
      </Typography>
    </Box>
  );
}

const OverviewTab = ({ data }) => {
  const { kpis, trends, health_distribution: health, range } = data;
  const comparisonLabel = range?.comparison_label;
  const comparable = range?.comparable !== false;
  const scope = range?.label;

  const kpiDefs = [
    { label: "Recruiter logins", k: "recruiter_visits", color: T.primary },
    { label: "CV reveals", k: "cv_reveals", color: T.amber },
    { label: "Applications", k: "applications", color: T.blue },
    { label: "New candidates", k: "new_candidates", color: T.teal },
    { label: "Hires", k: "hires", color: T.green },
  ];

  const healthData = Object.entries(health || {}).map(([key, value]) => ({
    key, name: HEALTH_LABELS[key] || key, value,
  }));

  return (
    <Stack spacing={3}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" }, gap: "16px" }}>
        {kpiDefs.map((d) => (
          <MetricCard
            key={d.k}
            label={d.label}
            current={kpis?.[d.k]?.current}
            previous={kpis?.[d.k]?.previous}
            comparisonLabel={comparisonLabel}
            comparable={comparable}
            barColor={d.color}
            scope={scope}
          />
        ))}
      </Box>

      <Box component={motion.div} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: "16px" }}>
          <Box sx={{ ...cardSx, display: "flex", flexDirection: "column" }}>
            <Typography sx={{ ...labelSx, mb: 1.5 }}>Platform trends (last 12 months)</Typography>
            {trends && trends.length > 0 ? (
              <Box sx={{ flex: 1, minHeight: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" vertical={false} />
                    <XAxis dataKey="month" tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={chartTooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif" }} />
                    {TREND_SERIES.map((s) => (
                      <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyNote />
            )}
          </Box>

          <Box sx={cardSx}>
            <SectionLabel>Company health</SectionLabel>
            {healthData.some((d) => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={healthData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={82} paddingAngle={2}>
                    {healthData.map((d) => (
                      <Cell key={d.key} fill={HEALTH_COLORS[d.key] || T.dim} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif" }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyNote />
            )}
            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
              <HealthLegend color={T.green} title="Active">
                Holds an unexpired credit and used the platform recently: a Kanban move, CV reveal or login in the last 4 weeks and no other risk signals.
              </HealthLegend>
              <HealthLegend color={T.amber} title="At risk">
                Trips at least one warning: stale Kanban, CV-reveal or login activity, or licence credits expiring unused.
              </HealthLegend>
              <HealthLegend color={T.red} title="Dormant">
                No Kanban moves, no CV reveals and no logins in the last 4 weeks, and no email engagement either: effectively not using the platform.
              </HealthLegend>
              <HealthLegend color={T.blue} title="Email openers">
                Only picked from dormant companies: they no longer use the platform, but still open or click our mails over a 1+ year span without ever logging in or acting in-app.
              </HealthLegend>
              <HealthLegend color={T.dim} title="Old clients">
                Had credits with us, all of which have now expired.
              </HealthLegend>
              <HealthLegend color={NO_CREDITS_COLOR} title="No credits">
                Never had a credit with us.
              </HealthLegend>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};

export default OverviewTab;
